/**
 * onboardingDraft — RW-2266 (F6).
 *
 * The welcome wizard is a blocking modal over every route, and until now a
 * refresh threw the answers away ("Your answers aren't saved until you finish
 * — refreshing starts over", which the copy said out loud). Nothing is written
 * to Firestore until the last step by design (RW-729/RW-1203: the profile
 * write is what completes the profile), so the resume point lives in this
 * browser instead, per user.
 *
 * Only what the person typed into the wizard is kept, and it is cleared the
 * moment the profile write succeeds.
 */

const PREFIX = 'reve_onboarding_step_';

/** Steps a draft may resume to: past the welcome, before the celebration. */
const FIRST_RESUMABLE = 2;
const LAST_RESUMABLE = 4;

export function draftKey(uid) {
  return `${PREFIX}${uid}`;
}

const FIELDS = ['displayName', 'phone', 'office', 'titleLabel', 'bio', 'specialties', 'serviceAreas', 'previewUrl'];

/**
 * The saved draft, or null when there is nothing usable. A step outside the
 * resumable range (a stale "complete", a hand-edited value) is dropped rather
 * than trusted — a wizard that opens on step 9 is worse than one that restarts.
 */
export function readDraft(uid) {
  if (!uid) return null;
  let raw = null;
  try { raw = localStorage.getItem(draftKey(uid)); } catch { return null; }
  if (!raw) return null;
  let parsed;
  try { parsed = JSON.parse(raw); } catch { return null; }
  if (!parsed || typeof parsed !== 'object') return null;
  const step = Number(parsed.step);
  if (!Number.isInteger(step) || step < FIRST_RESUMABLE || step > LAST_RESUMABLE) return null;
  const out = { step };
  for (const f of FIELDS) {
    if (parsed[f] !== undefined && parsed[f] !== null) out[f] = parsed[f];
  }
  return out;
}

/** Write the resume point. A step outside the range clears the draft instead. */
export function writeDraft(uid, draft) {
  if (!uid || !draft) return;
  const step = Number(draft.step);
  if (!Number.isInteger(step) || step < FIRST_RESUMABLE || step > LAST_RESUMABLE) {
    clearDraft(uid);
    return;
  }
  const payload = { step };
  for (const f of FIELDS) {
    if (draft[f] !== undefined) payload[f] = draft[f];
  }
  try { localStorage.setItem(draftKey(uid), JSON.stringify(payload)); } catch {}
}

export function clearDraft(uid) {
  if (!uid) return;
  try { localStorage.removeItem(draftKey(uid)); } catch {}
}
